import { isEmpty, omit, toLower } from '../../util/lodashlike';
import {
  axios,
  REPEATED_PARAMS_SERIALIZER,
  omitEmptyArrayFilters
} from './common';
import { CM_BASE_URL } from '../../pages/constants/cmConstants';
import { convertFilterEpochForBackend } from '../../util/timezone';

export const fetchAlerts = async ({ filters, currentPage, pageSize }) => {
  const params = omitEmptyArrayFilters({
    level: filters.alertLevel,
    typologyIds: !isEmpty(filters.typologyIds)
      ? filters.typologyIds.join(',')
      : null,
    sources: !isEmpty(filters.sourceIds) ? filters.sourceIds.join(',') : null,
    ...omit(filters, [
      'alertLevel',
      'searchField',
      'searchText',
      'typologyIds',
      'sourceIds'
    ]),
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
    from: convertFilterEpochForBackend(filters.from),
    to: convertFilterEpochForBackend(filters.to)
  });

  if (filters.searchField && filters.searchText) {
    // FIN-13755 — an Activity ID is matched exactly and case-sensitively (StarRocks), so it goes
    // out as typed; the other fields are matched by CM with a case-insensitive LIKE.
    params[filters.searchField] =
      filters.searchField === 'activity-id'
        ? filters.searchText
        : toLower(filters.searchText);
  }

  const url = `${CM_BASE_URL}/alerts`;

  // FIN-12644: the multi-select watchlist filters go out as repeated params, not CSV.
  return axios
    .get(url, { params, paramsSerializer: REPEATED_PARAMS_SERIALIZER })
    .then((res) => {
      if (res.status !== 200) {
        throw new Error('Failed to fetch alerts');
      }
      return res.data;
    });
};

export const fetchAlertDetailsById = async (alertId) => {
  const url = `${CM_BASE_URL}/alerts/byId/${alertId}`;
  const res = await axios.get(url);

  if (res.status !== 200 && res.status !== 404) {
    throw new Error(res.data.error ?? 'Error fetching alert details');
  } else if (res.status === 404) {
    throw new Error("You don't have access to this alert");
  }

  return res.data;
};

export const fetchWhitelistByCustomer = async (alertId) => {
  const url = `${CM_BASE_URL}/alerts/${alertId}/whitelists`;
  const res = await axios.get(url);

  if (res.status !== 200 && res.status !== 404) {
    throw new Error(res.data.error ?? 'Error fetching alert details');
  } else if (res.status === 404) {
    throw new Error("You don't have access to this alert");
  }

  return res.data;
};

export const updateWhitelistByCustomer = async (payload) => {
  const { startDate, endDate, notes, alertId } = payload;

  const requestBody = {
    startDate,
    endDate,
    notes
  };
  const url = `${CM_BASE_URL}/alerts/${alertId}/whitelists`;
  const res = await axios.put(url, requestBody);

  if (res.status !== 200 && res.status !== 404 && res.status !== 202) {
    throw new Error(res.data.error ?? 'Error fetching alert details');
  } else if (res.status === 404) {
    throw new Error("You don't have access to this alert");
  }

  return res.data;
};

export const changeAlertStatus = async ({ alertId, status }) => {
  const url = `${CM_BASE_URL}/alerts/${alertId}/status/${status}`;

  return axios.put(url).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      // FIN-12885: CM error bodies are { error, code } — `.msg` never exists, so the
      // typed BE message (e.g. the invalid-transition detail) was always swallowed.
      throw new Error(
        res.data?.error ?? res.data?.msg ?? 'Failed to change alert status'
      );
    }
    return res.data;
  });
};

export const assignAlertsToUser = async ({ alertIds, userId }) => {
  const requestBody = {
    alertIds,
    user: userId
  };

  const url = `${CM_BASE_URL}/alerts/assignToUser`;

  return axios.put(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      // FIN-12885: same `.msg`-vs-`.error` mismatch as changeAlertStatus above.
      throw new Error(
        res.data?.error ??
          res.data?.msg ??
          'Something went wrong. Please try again after sometime.'
      );
    }
    return res.data;
  });
};

export const addAlertNote = async ({ alertId, alertNote }) => {
  const requestBody = {
    alertId,
    note: alertNote
  };

  const url = `${CM_BASE_URL}/history/addNote`;

  return axios.post(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw { message: 'Failed to add alert note' };
    }
    return res.data;
  });
};

export const createCaseFromAlerts = async ({ alertIds, departmentId }) => {
  const requestBody = {
    alertIds,
    departmentId
  };
  const url = `${CM_BASE_URL}/cases/fromAlerts`;
  return axios.post(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data?.status !== 'success') {
      const data = res.data || {};
      // FIN-13684: a 409 ALERTS_ALREADY_CASED carries `alreadyCased` [{alertId, caseId}], but
      // `error` only names the alerts — same gap FIN-13218 closed for mergeAlertsToCase below.
      // Name the owning case so every Create Case consumer (FRAML queue, FRAML customer detail,
      // CM alert-list Convert) can tell the analyst where the alert already lives.
      const cased = Array.isArray(data.alreadyCased) ? data.alreadyCased : [];
      const message = cased.length
        ? `Already part of a case: ${cased
            .map((c) => `${c.alertId} \u2192 case ${c.caseId}`)
            .join(', ')}`
        : data.error;
      const err = new Error(message ?? 'Failed to convert alert(s) to a case');
      // FIN-13958 — structured 409 detail for callers that fold many requests into one message.
      if (cased.length) {
        err.alreadyCased = cased;
        err.code = data.code;
      }
      throw err;
    }
    return res.data;
  });
};

export const mergeAlertsToCase = async ({ alertIds, caseId }) => {
  const requestBody = {
    alertIds,
    caseId
  };
  const url = `${CM_BASE_URL}/cases/${caseId}/addAlerts`;
  return axios.put(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      const data = res.data || {};
      // FIN-12556: the BE returns two error shapes for this route. A bad (non-numeric)
      // caseId is a 400 ParamValidationFailed whose specific reason ("Case ID must be
      // numeric.") is in `requestValidationErrors`, with a generic `error` wrapper
      // ("Request Validation Failed"). A missing case is a 404 CMAppError whose reason
      // ("Case <id> not found.") is in `error`. Surface the specific validation
      // message when present; otherwise fall back to `error`.
      //
      // FIN-13218: a 409 ALERTS_ALREADY_CASED carries `alreadyCased` [{alertId, caseId}]
      // (FIN-13084 added it BE-side), but `error` only names the alerts, so the analyst was
      // told the alert was already cased with no way to reach the owning case. Naming the
      // case here fixes every consumer at once — Case Detail > Add to Case calls this
      // directly, and the merge saga falls back to `.message` when there is no `.response`.
      const cased = Array.isArray(data.alreadyCased) ? data.alreadyCased : [];
      const message =
        Array.isArray(data.requestValidationErrors) &&
        data.requestValidationErrors.length
          ? data.requestValidationErrors.join(' ')
          : cased.length
          ? `Already part of a case: ${cased
              .map((c) => `${c.alertId} \u2192 case ${c.caseId}`)
              .join(', ')}`
          : data.error;
      // FIN-13958 — structured 409 detail for callers that fold many requests into one message.
      // Only on a 409 that names the cased alerts; every other rejection stays exactly { message }.
      throw cased.length
        ? { message, alreadyCased: cased, code: data.code }
        : { message };
    }
    return res.data;
  });
};

// FIN-10455: Suppress raw BE errors (which leak Scala Option values and
// internal IDs) and show one of two PO-approved messages:
//   - 401/403 or body code AUTH_ATTRIBUTEERROR → authorization message
//   - anything else → generic hard-failure message
// FIN-10510 (re-open 2026-04-23): Also match the BE's AttributeAuth response
// body code, since HTTP status can be rewritten by proxies/gateways and the
// body is the authoritative signal that BE rejected on authorization grounds.
// FIN-10455 (re-open 2026-04-27): For the RE_OPENED transition only, mirror
// the alert-details page and surface the BE's per-user `msg` (e.g.
// "User <email> is not authorized to reopen the alert"). All other
// transitions still use the static AUTH_MSG so we don't leak Scala-Option
// debug strings. The auth-detection logic lives in both the try (axios is
// configured with validateStatus:false so non-2xx lands here) and the catch
// (interceptor- or network-rejected paths).
// FIN-13970: SUPERSEDES the reopen-only passthrough above, as FIN-10905 did for
// cases. The BE's bulk-alert auth messages are now user-friendly and name the
// users involved (no Scala Option / Department dumps), so surface `msg` for
// every status; fall back to AUTH_MSG only when the body has no `msg`.
export const bulkUpdateAlertStatus = async ({ alertIds, status, reason }) => {
  const url = `${CM_BASE_URL}/alerts/bulkUpdateStatus`;
  const AUTH_MSG =
    'You are not authorized to bulk update alerts assigned to other users.';
  const GENERIC_MSG =
    'Bulk update could not be completed. Please try again or contact your system administrator.';
  const pickAuthMsg = (data) => data?.msg || AUTH_MSG;
  // FIN-13957 — a 400 Request Validation Failed carries curated, user-facing reasons in
  // `requestValidationErrors` (e.g. "Maximum 100 alerts per bulk update", "Reason must not
  // exceed 5000 characters"). Those are safe to show and tell the user what to change; the
  // FIN-10455 generic message hid them. Only this field is passed through for non-auth failures;
  // auth failures follow FIN-13970 (pickAuthMsg above), everything else stays GENERIC_MSG.
  // A malformed body yields "Decoding JSON <json4s exception text>" in the same list — internal, not
  // curated — so that one is never shown (review L7).
  const pickValidationMsg = (httpStatus, data) => {
    if (httpStatus !== 400 || !Array.isArray(data?.requestValidationErrors))
      return null;
    const curated = data.requestValidationErrors.filter(
      (m) => typeof m === 'string' && m && !m.startsWith('Decoding JSON')
    );
    return curated.length > 0 ? curated.join(' ') : null;
  };

  try {
    const res = await axios.post(url, { alertIds, status, reason });
    if (res.status === 200) return res.data;
    const code = res.data?.code;
    const isAuth =
      res.status === 401 ||
      res.status === 403 ||
      code === 'AUTH_ATTRIBUTEERROR';
    if (isAuth) throw new Error(pickAuthMsg(res.data));
    throw new Error(pickValidationMsg(res.status, res.data) || GENERIC_MSG);
  } catch (error) {
    if (error.message === AUTH_MSG || error.message === GENERIC_MSG) {
      throw error;
    }
    const httpStatus = error.response?.status;
    const responseData = error.response?.data;
    const code = responseData?.code;
    const isAuth =
      httpStatus === 401 ||
      httpStatus === 403 ||
      code === 'AUTH_ATTRIBUTEERROR';
    if (isAuth) throw new Error(pickAuthMsg(responseData));
    const validationMsg = pickValidationMsg(httpStatus, responseData);
    if (validationMsg) throw new Error(validationMsg);
    // Re-throw a custom auth msg we deliberately produced in the try branch
    // (e.g. the RE_OPENED BE-detail passthrough), which is always a real Error
    // instance. A raw transport rejection (no `response`, a plain
    // `{ message: 'Network Error' }` object) is NOT one of ours, so it falls
    // through to the PO-approved generic message instead of leaking the raw
    // axios string to the UI. (FIN-10455 / FE-X-15e)
    if (error instanceof Error && error.message && error.message !== '') {
      throw error;
    }
    throw new Error(GENERIC_MSG);
  }
};

// FIN-10888 / FIN-10527: Suppress raw BE errors (which leak Scala Option values
// and case IDs) and show one of two PO-approved messages:
//   - 401/403 or body code AUTH_ATTRIBUTEERROR → authorization message
//   - anything else → generic hard-failure message
// FIN-10515: Accept doChecker for checker review workflow
// FIN-10510 (re-open 2026-04-23): Also match the BE's AttributeAuth response
// body code, since HTTP status can be rewritten by proxies/gateways and the
// body is the authoritative signal that BE rejected on authorization grounds.
// FIN-10905 (re-open 2026-04-29, QA Done 2026-05-09): SUPERSEDES the
// FIN-10527 / FIN-10888 "static AUTH_MSG only" reversion above. The BE no
// longer emits raw Scala-Option debug strings — `msg` is now a user-friendly
// sentence explaining the specific reason (e.g. "You must self-assign this
// case before you can close it"). Surface it directly so the user understands
// why the action was rejected; fall back to the static AUTH_MSG only when the
// BE body has no usable `msg`.
export const bulkUpdateCaseStatus = async ({
  caseIds,
  status,
  reason,
  doChecker
}) => {
  const url = `${CM_BASE_URL}/cases/bulkUpdateStatus`;
  const AUTH_MSG =
    'You are not authorized to bulk update cases assigned to other users.';
  const GENERIC_MSG =
    'Bulk update could not be completed. Please try again or contact your system administrator.';

  // FIN-10905 (re-open 2026-04-29): BE has cleaned up the Scala-Option
  // debug strings — `msg` is now a user-friendly sentence explaining the
  // specific reason (e.g. cross-dept, must self-assign). Surface it directly
  // so the user understands why the action was rejected. Falls back to
  // AUTH_MSG if `msg` is missing or empty.
  const pickAuthMsg = (data) => {
    const msg = data?.msg;
    return typeof msg === 'string' && msg.trim() !== '' ? msg : AUTH_MSG;
  };

  try {
    const res = await axios.post(url, { caseIds, status, reason, doChecker });
    if (res.status === 200) return res.data;
    const code = res.data?.code;
    const isAuth =
      res.status === 401 ||
      res.status === 403 ||
      code === 'AUTH_ATTRIBUTEERROR';
    throw new Error(isAuth ? pickAuthMsg(res.data) : GENERIC_MSG);
  } catch (error) {
    if (error.message === GENERIC_MSG) {
      throw error;
    }
    const httpStatus = error.response?.status;
    const responseData = error.response?.data;
    const code = responseData?.code;
    if (
      httpStatus === 401 ||
      httpStatus === 403 ||
      code === 'AUTH_ATTRIBUTEERROR'
    ) {
      throw new Error(pickAuthMsg(responseData));
    }
    // Re-throw any custom auth msg already produced (e.g. BE detail msg from try)
    if (error.message) throw error;
    throw new Error(GENERIC_MSG);
  }
};

// FIN-10098: Apply alert status to other alerts in case
// FIN-12254: Suppress raw BE errors and show a static, approved message for
// auth-classified failures (case not found / cross-department / Investigator
// attempting to update a closed-resolved alert all throw AttributeAuth on the
// backend and are indistinguishable here) — matches the bulkUpdateAlertStatus
// / bulkUpdateCaseStatus convention above, adopted after two prior attempts to
// pass the BE's raw `msg` straight through leaked internal debug strings
// (FIN-10905 revert, FIN-10527/FIN-10888). `code === 'AUTH_ATTRIBUTEERROR'` is
// checked in addition to the HTTP status since the status code for this rule
// may be 404 or 403 depending on backend deploy state (FIN-12254 changes it
// to 403; the body code is the authoritative signal either way).
export const applyAlertStatusBulkInCase = async ({
  caseId,
  alertIds,
  targetStatus,
  reason,
  triggeringAlertId
}) => {
  const url = `${CM_BASE_URL}/alerts/bulk-status-in-case`;
  const AUTH_MSG =
    'You are not authorized to update alerts that are already closed or resolved.';
  const GENERIC_MSG =
    'Bulk update could not be completed. Please try again or contact your system administrator.';

  try {
    const res = await axios.put(url, {
      caseId,
      alertIds,
      targetStatus,
      reason,
      triggeringAlertId
    });
    if (res.status === 200) return res.data;
    const code = res.data?.code;
    const isAuth =
      res.status === 401 ||
      res.status === 403 ||
      code === 'AUTH_ATTRIBUTEERROR';
    throw new Error(isAuth ? AUTH_MSG : GENERIC_MSG);
  } catch (error) {
    if (error.message === AUTH_MSG || error.message === GENERIC_MSG) {
      throw error;
    }
    const httpStatus = error.response?.status;
    const code = error.response?.data?.code;
    const isAuth =
      httpStatus === 401 ||
      httpStatus === 403 ||
      code === 'AUTH_ATTRIBUTEERROR';
    throw new Error(isAuth ? AUTH_MSG : GENERIC_MSG);
  }
};

export const fetchAlertsRelayHistoryList = async ({
  filters = [],
  currentPage,
  pageSize
}) => {
  const params = {
    status: filters?.status,
    isLatest: filters?.isLatest,
    offset: (currentPage - 1) * pageSize,
    limit: pageSize
  };

  if (filters.searchField && filters.searchText) {
    params[filters.searchField] = toLower(filters.searchText);
  }

  const url = `${CM_BASE_URL}/retry-history/all`;

  return axios.get(url, { params }).then((res) => {
    if (res.status !== 200) {
      throw new Error('Failed to fetch alert retry history');
    }
    return res.data;
  });
};
